/* @jsx h */
import { computed, defineComponent, h, ref } from "vue";
import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";
import { RightSheet } from "@lemmonhq/uikit";
import { setTitle } from "@lemmonhq/orders";
import { useTrpc } from "@app/vue/providers/TrpcProvider";
import { useToast } from "@app/vue/providers/index";
import type { AdminRestaurantRecord } from "@lemmonhq/types";
import {
  RestaurantForm,
  type RestaurantFormValue,
} from "../components/RestaurantForm";

export const AdminRestaurantsPage = defineComponent({
  name: "AdminRestaurantsPage",
  setup() {
    setTitle("Admin Restaurants");

    const trpc = useTrpc();
    const toast = useToast();
    const queryClient = useQueryClient();
    const search = ref("");
    const sheetOpen = ref(false);
    const selectedRestaurant = ref<AdminRestaurantRecord | null>(null);

    const restaurantsQuery = useQuery({
      queryKey: ["admin", "restaurants", "list"],
      queryFn: () => trpc.admin.restaurants.list.query(),
    });

    const shopGroupsQuery = useQuery({
      queryKey: ["admin", "shopGroups"],
      queryFn: () => trpc.admin.shopGroups.query(),
    });

    const shopGroupOptions = computed(() =>
      (shopGroupsQuery.data.value || []).map((group) => ({
        id: group.id,
        name: group.name,
      })),
    );

    const createMutation = useMutation({
      mutationFn: (input: RestaurantFormValue) =>
        trpc.admin.restaurants.create.mutate(input),
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: ["admin", "restaurants"],
        });
        await queryClient.invalidateQueries({
          queryKey: ["admin", "dashboard"],
        });
        await queryClient.invalidateQueries({
          queryKey: ["admin", "shopGroups"],
        });
        toast.showToast("Restaurant created successfully.", {
          type: "success",
        });
        sheetOpen.value = false;
      },
      onError: (error) => {
        toast.showToast(error.message || "Failed to create restaurant.", {
          type: "error",
        });
      },
    });

    const updateMutation = useMutation({
      mutationFn: (input: {
        id: number;
        data: Omit<RestaurantFormValue, "manager_password"> & {
          manager_password: string | null;
        };
      }) =>
        trpc.admin.restaurants.update.mutate(input),
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: ["admin", "restaurants"],
        });
        await queryClient.invalidateQueries({
          queryKey: ["admin", "dashboard"],
        });
        toast.showToast("Restaurant updated successfully.", {
          type: "success",
        });
        sheetOpen.value = false;
        selectedRestaurant.value = null;
      },
      onError: (error) => {
        toast.showToast(error.message || "Failed to update restaurant.", {
          type: "error",
        });
      },
    });

    const filteredRestaurants = computed(() => {
      const query = search.value.trim().toLowerCase();
      const rows = restaurantsQuery.data.value || [];
      if (!query) {
        return rows;
      }
      return rows.filter((restaurant) =>
        [
          restaurant.name,
          restaurant.slug || "",
          restaurant.email,
          restaurant.contact_person,
          restaurant.manager.name || "",
          restaurant.manager.email || "",
        ].some((value) => value.toLowerCase().includes(query)),
      );
    });

    const submitForm = async (value: RestaurantFormValue) => {
      if (selectedRestaurant.value) {
        await updateMutation.mutateAsync({
          id: selectedRestaurant.value.id,
          data: {
            ...value,
            manager_id: selectedRestaurant.value.manager.id,
            manager_password: value.manager_password || null,
          },
        });
        return;
      }

      await createMutation.mutateAsync(value);
    };

    const isSaving = computed(
      () => createMutation.isPending.value || updateMutation.isPending.value,
    );

    return () => (
      <div class="flex w-full min-h-0 flex-col overflow-y-auto overflow-x-hidden">
        <div class="sticky top-0 z-[1] border-b border-base-300/60 bg-base-100/95 px-4 py-4 backdrop-blur">
          <div class="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
            <div>
              <div class="mb-2 inline-flex items-center gap-2 rounded-full border border-accent/20 bg-accent/10 px-3 py-1 text-xs font-medium uppercase tracking-[0.18em] text-accent">
                <span class="material-symbols-outlined text-sm">
                  storefront
                </span>
                Restaurant directory
              </div>
              <h2 class="text-2xl font-semibold text-accent">Restaurants</h2>
              <p class="text-sm text-base-content/60">
                Create and maintain restaurant workspaces and their manager
                accounts.
              </p>
            </div>

            <div class="flex flex-col gap-3 sm:flex-row">
              <label class="input input-bordered flex items-center gap-2 w-full sm:w-[320px]">
                <span class="material-symbols-outlined text-base-content/60">
                  search
                </span>
                <input
                  class="grow"
                  value={search.value}
                  placeholder="Search restaurants or managers"
                  onInput={(event) => {
                    search.value = (event.target as HTMLInputElement).value;
                  }}
                />
              </label>

              <button
                class="btn btn-primary"
                onClick={() => {
                  selectedRestaurant.value = null;
                  sheetOpen.value = true;
                }}
              >
                <span class="material-symbols-outlined text-base">add</span>
                New restaurant
              </button>
            </div>
          </div>
        </div>

        <div class="flex flex-col gap-6 p-4">
          <div class="grid gap-4 md:grid-cols-3">
            <div class="rounded-2xl border border-base-300/60 bg-gradient-to-br from-base-100 to-base-200/40 p-5 shadow-sm">
              <div class="text-xs font-semibold uppercase tracking-[0.18em] text-accent">
                Restaurants
              </div>
              <div class="mt-2 text-3xl font-semibold text-accent">
                {restaurantsQuery.data.value?.length || 0}
              </div>
              <div class="mt-2 text-sm text-base-content/60">
                Total active records available to admin.
              </div>
            </div>
            <div class="rounded-2xl border border-base-300/60 bg-gradient-to-br from-base-100 to-base-200/40 p-5 shadow-sm">
              <div class="text-xs font-semibold uppercase tracking-[0.18em] text-accent">
                Visible
              </div>
              <div class="mt-2 text-3xl font-semibold text-accent">
                {filteredRestaurants.value.length}
              </div>
              <div class="mt-2 text-sm text-base-content/60">
                Matching the current search criteria.
              </div>
            </div>
            <div class="rounded-2xl border border-base-300/60 bg-gradient-to-br from-base-100 to-base-200/40 p-5 shadow-sm">
              <div class="text-xs font-semibold uppercase tracking-[0.18em] text-accent">
                Managers
              </div>
              <div class="mt-2 text-3xl font-semibold text-accent">
                {
                  (restaurantsQuery.data.value || []).filter(
                    (restaurant) => !!restaurant.manager.id,
                  ).length
                }
              </div>
              <div class="mt-2 text-sm text-base-content/60">
                Restaurants with a primary manager attached.
              </div>
            </div>
          </div>

          {restaurantsQuery.isPending.value ? (
            <div class="rounded-3xl border border-base-300/60 bg-base-100 px-8 py-20 text-center">
              <span class="loading loading-spinner loading-lg text-primary"></span>
            </div>
          ) : restaurantsQuery.error.value ? (
            <div class="rounded-3xl border border-error/40 bg-error/10 px-8 py-16 text-center">
              <div class="text-lg font-semibold text-error">
                Failed to load restaurants
              </div>
              <div class="mt-2 text-sm text-base-content/70">
                Refresh the page and try again.
              </div>
            </div>
          ) : (
            <div class="overflow-hidden rounded-3xl border border-base-300/60 bg-base-100 shadow-sm">
              <div class="border-b border-base-300/60 bg-gradient-to-r from-accent/10 to-transparent px-5 py-4">
                <h3 class="text-lg font-semibold text-accent">
                  Restaurant directory
                </h3>
                <p class="text-sm text-base-content/60">
                  Select a row to edit the restaurant profile and manager
                  assignment.
                </p>
              </div>

              <div class="overflow-x-auto">
                <table class="table table-zebra">
                  <thead>
                    <tr>
                      <th class="text-accent">Restaurant</th>
                      <th class="text-accent">Manager</th>
                      <th class="text-accent">Status</th>
                      <th class="text-accent">Locale</th>
                      <th class="text-accent">Contact</th>
                      <th class="text-accent text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRestaurants.value.map((restaurant) => (
                      <tr
                        key={restaurant.id}
                        class="cursor-pointer hover"
                        onClick={() => {
                          selectedRestaurant.value = restaurant;
                          sheetOpen.value = true;
                        }}
                      >
                        <td>
                          <div class="font-semibold text-accent">
                            {restaurant.name}
                          </div>
                          <div class="text-xs text-base-content/55">
                            /{restaurant.slug || "no-slug"}
                          </div>
                          <div class="mt-1 text-xs text-base-content/65">
                            {restaurant.address}
                          </div>
                        </td>
                        <td>
                          <div class="font-medium">
                            {restaurant.manager.name || "Not assigned"}
                          </div>
                          <div class="text-xs text-base-content/60">
                            {restaurant.manager.email || "No email"}
                          </div>
                        </td>
                        <td>
                          <span class="badge badge-outline border-accent/40 text-accent">
                            {restaurant.status}
                          </span>
                        </td>
                        <td>
                          <div class="font-medium">
                            {restaurant.locale || "-"}
                          </div>
                          <div class="text-xs text-base-content/60">
                            {restaurant.currency || "-"} /{" "}
                            {restaurant.jurisdiction || "-"}
                          </div>
                        </td>
                        <td>
                          <div class="font-medium">{restaurant.email}</div>
                          <div class="text-xs text-base-content/60">
                            {restaurant.contact_person}
                          </div>
                        </td>
                        <td class="text-right">
                          <button
                            type="button"
                            class="btn btn-sm btn-ghost text-accent"
                            onClick={(event) => {
                              event.stopPropagation();
                              selectedRestaurant.value = restaurant;
                              sheetOpen.value = true;
                            }}
                          >
                            <span class="material-symbols-outlined text-base">
                              edit
                            </span>
                            Edit
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {filteredRestaurants.value.length === 0 ? (
                  <div class="px-6 py-12 text-center text-sm text-base-content/60">
                    No restaurants matched the current search.
                  </div>
                ) : null}
              </div>
            </div>
          )}
        </div>

        <RightSheet
          isOpen={sheetOpen.value}
          title={
            selectedRestaurant.value ? "Edit restaurant" : "Create restaurant"
          }
          onClose={() => {
            if (isSaving.value) {
              return;
            }
            sheetOpen.value = false;
            selectedRestaurant.value = null;
          }}
          width="760px"
        >
          <RestaurantForm
            restaurant={selectedRestaurant.value}
            submitting={isSaving.value}
            shopGroups={shopGroupOptions.value}
            onSubmit={submitForm}
            onCancel={() => {
              if (isSaving.value) {
                return;
              }
              sheetOpen.value = false;
              selectedRestaurant.value = null;
            }}
          />
        </RightSheet>
      </div>
    );
  },
});
